'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import { getConsent, onConsentChange, type ConsentState } from './consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    google_tag_data?: {
      ics?: {
        entries?: Record<string, Record<string, unknown>>;
        active?: boolean;
        usedDefault?: boolean;
        usedUpdate?: boolean;
        usedImplicit?: boolean;
        wasSetLate?: boolean;
        waitPeriodTimedOut?: boolean;
      };
      tidr?: {
        container?: Record<string, Record<string, unknown>>;
        pending?: Array<{ target?: { ctid?: string } }>;
      };
    };
    google_tag_manager?: Record<string, unknown>;
  }
}

const SESSION_STORAGE_KEY = 'marketing.audit.session';
const GTM_ID = 'GTM-TX3J78RL';
const AUDIT_ENDPOINT = '/api/marketing/consent-audit';

type AuditPhase = 'initial' | 'consent_update';

function getSessionId() {
  if (typeof window === 'undefined') return 'server';

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const next = crypto.randomUUID();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
  return next;
}

function parseStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem('consent.v1');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;

    if (
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.ads === 'boolean'
    ) {
      return { analytics: parsed.analytics, ads: parsed.ads };
    }
  } catch {
    return null;
  }

  return null;
}

function summarizeDataLayerEntry(entry: unknown) {
  if (Array.isArray(entry)) {
    const arrayEntry = entry as unknown[];
    const command: unknown = arrayEntry[0];
    const action: unknown = arrayEntry[1];
    const payload: unknown = arrayEntry[2];
    const payloadSummary: unknown =
      payload && typeof payload === 'object'
        ? Object.fromEntries(
            Object.entries(payload as Record<string, unknown>).slice(0, 8),
          )
        : payload ?? null;

    return {
      kind: 'array',
      command: typeof command === 'string' ? command : null,
      action: typeof action === 'string' ? action : null,
      payload: payloadSummary,
    };
  }

  if (entry && typeof entry === 'object') {
    const eventRecord = entry as Record<string, unknown>;
    const event = eventRecord.event;
    const gtmRecord =
      eventRecord.gtm && typeof eventRecord.gtm === 'object'
        ? (eventRecord.gtm as Record<string, unknown>)
        : null;
    const uniqueEventId = gtmRecord?.uniqueEventId;

    return {
      kind: 'object',
      event: typeof event === 'string' ? event : null,
      unique_event_id:
        typeof uniqueEventId === 'number' ? uniqueEventId : null,
      keys: Object.keys(entry as Record<string, unknown>).slice(0, 8),
    };
  }

  return {
    kind: typeof entry,
    value:
      typeof entry === 'string' || typeof entry === 'number' || entry === null
        ? entry
        : null,
  };
}

function buildAuditSnapshot(pathname: string, consent: ConsentState | null) {
  const dataLayer = Array.isArray(window.dataLayer) ? window.dataLayer : [];
  const runtimeConsent = window.google_tag_data?.ics;
  const containerStates = window.google_tag_data?.tidr?.container ?? {};
  const pendingDestinations = window.google_tag_data?.tidr?.pending ?? [];

  return {
    eventId: crypto.randomUUID(),
    sessionId: getSessionId(),
    href: window.location.href,
    path: pathname,
    gtmId: GTM_ID,
    consent,
    storedConsent: parseStoredConsent(),
    bannerPresent: Boolean(
      document.querySelector('dialog[aria-label="Consentimento de cookies"]'),
    ),
    documentReadyState: document.readyState,
    visibilityState: document.visibilityState,
    dataLayerLength: dataLayer.length,
    dataLayerPreview: dataLayer.slice(0, 8).map(summarizeDataLayerEntry),
    runtimeConsent: runtimeConsent
      ? {
          active: runtimeConsent.active ?? null,
          usedDefault: runtimeConsent.usedDefault ?? null,
          usedUpdate: runtimeConsent.usedUpdate ?? null,
          usedImplicit: runtimeConsent.usedImplicit ?? null,
          wasSetLate: runtimeConsent.wasSetLate ?? null,
          waitPeriodTimedOut: runtimeConsent.waitPeriodTimedOut ?? null,
          entries: runtimeConsent.entries ?? null,
        }
      : null,
    destinationStates: Object.fromEntries(
      Object.entries(containerStates).map(([key, value]) => [
        key,
        typeof value.state === 'number' ? value.state : null,
      ]),
    ),
    pendingDestinations: pendingDestinations
      .map((item) => item.target?.ctid ?? null)
      .filter((value): value is string => Boolean(value)),
  };
}

function sendAudit(payload: Record<string, unknown>) {
  const body = JSON.stringify(payload);

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon(AUDIT_ENDPOINT, blob)) {
      return;
    }
  }

  void fetch(AUDIT_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {});
}

export function GoogleTagConsentAudit() {
  const pathname = usePathname();
  const consent = useSyncExternalStore(
    (listener) => onConsentChange(() => listener()),
    () => getConsent(),
    () => null,
  );
  const initialTimerRef = useRef<number | null>(null);
  const lastConsentFingerprintRef = useRef<string | null>(null);
  const latestConsentRef = useRef<ConsentState | null>(consent);

  useEffect(() => {
    latestConsentRef.current = consent;
  }, [consent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (initialTimerRef.current) {
      window.clearTimeout(initialTimerRef.current);
    }

    initialTimerRef.current = window.setTimeout(() => {
      sendAudit({
        phase: 'initial' satisfies AuditPhase,
        ...buildAuditSnapshot(pathname, latestConsentRef.current),
      });
      initialTimerRef.current = null;
    }, 1500);

    return () => {
      if (initialTimerRef.current) {
        window.clearTimeout(initialTimerRef.current);
        initialTimerRef.current = null;
      }
    };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || consent === null) return;

    const fingerprint = JSON.stringify({ pathname, consent });
    if (lastConsentFingerprintRef.current === fingerprint) return;
    lastConsentFingerprintRef.current = fingerprint;

    window.setTimeout(() => {
      sendAudit({
        phase: 'consent_update' satisfies AuditPhase,
        ...buildAuditSnapshot(pathname, consent),
      });
    }, 300);
  }, [pathname, consent]);

  return null;
}
